import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ScrollText, Loader2, Search, ShieldCheck, ShieldAlert, Lock, ChevronLeft, ChevronRight, RotateCcw, Calendar as CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { de, fr, it, enGB } from "date-fns/locale";

import { PageHeader } from "@/components/control/PageHeader";
import { AuditExport } from "@/components/control/AuditExport";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Stable role keys stay internal; the log shows German display names.
const ROLE_DE = {
  super_admin: "Super Admin", admin: "Administration", reviewer: "Prüfer:in", moderator: "Moderator:in", support: "Support",
  editor: "Redaktion", org_staff: "Begrenzter Organisationszugang", member: "Mitglied", system: "System",
};
const roleLabel = (r) => (r ? ROLE_DE[r] || r : "—");
const SUBJECTS = {
  member: "Mitglied", saved_view: "Gespeicherte Ansicht", appeal: "Einspruch", org_claim: "Organisationsanspruch", support: "Supportfall",
  team: "Team", activity: "Aktivität", group: "Gruppe", report: "Meldung", content: "Inhalt", notification: "Benachrichtigung",
  review: "Profilprüfung", organization: "Organisation", incident: "Störung", auth: "Anmeldung", account: "Konto", access_pass: "Access Pass",
  retention: "Aufbewahrung", data_request: "Datenanfrage", location: "Standort", task: "Aufgabe", pilot: "Pilot", audit: "Audit",
};
const VERBS = {
  created: "erstellt", updated: "aktualisiert", deleted: "gelöscht", decided: "entschieden", invited: "eingeladen",
  invitation_revoked: "Einladung widerrufen", approved: "freigegeben", rejected: "abgelehnt", revoked: "widerrufen", assigned: "zugewiesen",
  resolved: "gelöst", reopened: "erneut geöffnet", submitted: "eingereicht", scheduled: "geplant", cancelled: "abgesagt", archived: "archiviert",
  paused: "pausiert", resumed: "fortgesetzt", exported: "exportiert", restricted: "eingeschränkt", lifted: "aufgehoben", note_added: "Notiz ergänzt",
  status_changed: "Status geändert", handed_over: "weitergegeben", requested: "angefragt", role_changed: "Rolle geändert",
  data_request_created: "Datenanfrage erfasst", password_changed: "Passwort geändert",
};
// Untranslated action keys are shown as "<Bereich>: <Vorgang>" instead of the raw key.
const fallbackAction = (a) => {
  const [s, ...rest] = String(a).split(".");
  const v = rest.join(".");
  return v ? `${SUBJECTS[s] || s.replace(/_/g, " ")}: ${VERBS[v] || v.replace(/[._]/g, " ")}` : a;
};
import { PageView, PageRows, PageButtons, Loadable, useLoad } from "@/components/control/kit";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { formatDateTime } from "@/lib/format";

const SEV_TONE = {
  info: "bg-zinc-100 text-zinc-700 border-zinc-300",
  warning: "bg-orange-100 text-orange-800 border-orange-300",
  critical: "bg-red-100 text-red-800 border-red-300",
};

export default function AuditLogs() {
  const { t, lang } = useI18n();

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [area, setArea] = useState("all");
  const [action, setAction] = useState("all");
  const [actor, setActor] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [securityOnly, setSecurityOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { aktion } = useParams();
  const eventPage = aktion?.startsWith("ereignis-") ? aktion : null;
  const openEvent = (e) => navigate(`/audit-log/aktion/ereignis-${e.event_id}`);

  const actionsMap = useMemo(() => t("audit.actions"), [t]);
  const labelAction = (a) => (actionsMap && actionsMap[a]) || fallbackAction(a);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setPage(1); }, [debouncedQ, area, action, actor, severity, securityOnly, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit", {
        params: {
          q: debouncedQ, area, action, actor, severity,
          security_only: securityOnly, date_from: dateFrom, date_to: dateTo,
          page, page_size: 20,
        },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, area, action, actor, severity, securityOnly, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setQ(""); setArea("all"); setAction("all"); setActor("all");
    setSeverity("all"); setSecurityOnly(false); setDateFrom(""); setDateTo("");
  };

  const filters = data?.filters || { areas: [], actions: [], actors: [] };
  const entries = data?.entries || [];

  return (
    <div data-testid="audit-page">
      <PageHeader title={t("audit.title")} subtitle={t("audit.subtitle")} icon={ScrollText} actions={<AuditExport />} />

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-brand-purple/15 bg-brand-purple/[0.04] px-4 py-2.5 text-sm text-brand-purple" data-testid="audit-readonly-note">
        <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t("audit.readonly_note")}
      </div>

      {/* Filters */}
      <Card className="mb-5 border-border/70">
        <CardContent className="space-y-4 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search"
              placeholder={t("audit.search_placeholder")} className="pl-9" aria-label={t("common.search")} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("audit.filter_area")}</label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger data-testid="audit-filter-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {filters.areas.map((a) => (<SelectItem key={a} value={a}>{t(`audit.area_label.${a}`)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("audit.filter_action")}</label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {filters.actions.map((a) => (<SelectItem key={a} value={a}>{labelAction(a)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("audit.filter_actor")}</label>
              <Select value={actor} onValueChange={setActor}>
                <SelectTrigger data-testid="audit-filter-actor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {filters.actors.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("audit.col_severity")}</label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="audit-filter-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="info">{t("audit.severity.info")}</SelectItem>
                  <SelectItem value="warning">{t("audit.severity.warning")}</SelectItem>
                  <SelectItem value="critical">{t("audit.severity.critical")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <DateFilter value={dateFrom} onChange={setDateFrom} label={t("audit.filter_from")} lang={lang} testid="audit-date-from" />
            <DateFilter value={dateTo} onChange={setDateTo} label={t("audit.filter_to")} lang={lang} testid="audit-date-to" />
            <label className="flex items-center gap-2 pb-2 text-sm text-brand-purple">
              <Switch checked={securityOnly} onCheckedChange={setSecurityOnly} data-testid="audit-security-toggle" />
              {t("audit.filter_security_only")}
            </label>
            <Button variant="outline" onClick={resetFilters} data-testid="audit-reset" className="mb-0.5 gap-1.5">
              <RotateCcw className="h-4 w-4" /> {t("common.reset")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/70">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand-magenta" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("audit.col_time")}</TableHead>
                    <TableHead>{t("audit.col_actor")}</TableHead>
                    <TableHead>{t("audit.col_area")}</TableHead>
                    <TableHead>{t("audit.col_action")}</TableHead>
                    <TableHead>{t("audit.col_status")}</TableHead>
                    <TableHead>{t("audit.col_security")}</TableHead>
                    <TableHead>{t("audit.col_event")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">{t("audit.no_results")}</TableCell></TableRow>
                  )}
                  {entries.map((e) => (
                    <TableRow key={e.event_id} data-testid={`audit-row-${e.event_id}`} tabIndex={0} aria-label={`Ereignis ${labelAction(e.action)} öffnen`}
                      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openEvent(e); } }}
                      className="cursor-pointer hover:bg-brand-purple/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-magenta" onClick={() => openEvent(e)}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatDateTime(e.timestamp, lang)}</TableCell>
                      <TableCell>
                        <p className="text-sm font-semibold text-brand-purple">{e.actor_email || "—"}</p>
                        <p className="text-xs text-muted-foreground">{roleLabel(e.actor_role)}</p>
                      </TableCell>
                      <TableCell className="text-sm">{t(`audit.area_label.${e.area}`)}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium text-brand-purple">{labelAction(e.action)}</p>
                        {e.target && <p className="text-xs text-muted-foreground">{e.target}</p>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={SEV_TONE[e.severity] || SEV_TONE.info}>
                          {t(`audit.status_label.${e.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {e.security_relevant ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600" title={t("audit.security_yes")}>
                            <ShieldAlert className="h-4 w-4" /> {t("audit.security_yes")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <ShieldCheck className="h-4 w-4" /> {t("audit.security_no")}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{e.event_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="text-sm text-muted-foreground" data-testid="audit-total">{data.total} {t("audit.showing")}</p>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              data-testid="audit-prev" className="gap-1"><ChevronLeft className="h-4 w-4" />{t("audit.prev")}</Button>
            <span className="text-sm text-brand-purple">{t("audit.page_of", { page: data.page, pages: data.pages })}</span>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
              data-testid="audit-next" className="gap-1">{t("audit.next")}<ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {eventPage && (
        <PageView page={eventPage} hideLink testId="audit-event" title="Audit-Ereignis im Detail"
          subtitle="Dieses Ereignis ist schreibgeschützt und zweckgebunden einsehbar." subject={eventPage.slice(9)}>
          {({ back }) => <AuditEvent id={eventPage.slice(9)} known={entries} back={back} t={t} lang={lang} labelAction={labelAction} />}
        </PageView>
      )}
    </div>
  );
}

function AuditEvent({ id, known, back, t, lang, labelAction }) {
  const cached = known.find((e) => e.event_id === id);
  const st = useLoad(cached ? null : `/audit?q=${encodeURIComponent(id)}&page_size=5`);
  const render = (e) => (!e ? <p className="rounded-xl bg-[#FFF6DD] px-4 py-3 text-sm text-[#8A5A00]" data-testid="audit-event-missing">Ereignis nicht gefunden.</p> : (
    <PageRows testId="audit-event" rows={[
      [t("audit.col_action"), labelAction(e.action)], [t("audit.col_actor"), `${e.actor_email || "—"} · ${roleLabel(e.actor_role)}`],
      [t("audit.col_time"), formatDateTime(e.timestamp, lang)], [t("audit.col_area"), t(`audit.area_label.${e.area}`)],
      [t("audit.col_status"), t(`audit.status_label.${e.status}`)], [t("audit.col_target"), e.target || "—"],
      [t("audit.col_severity"), t(`audit.severity.${e.severity}`)], [t("audit.col_security"), e.security_relevant ? t("audit.security_yes") : t("audit.security_no")],
      [t("audit.col_ip"), e.ip || "—"], [t("audit.col_session"), e.session_ref || "—"], [t("audit.col_event"), e.event_id]]} />
  ));
  return (
    <>
      {cached ? render(cached) : <Loadable state={st}>{(d) => render((d.entries || []).find((e) => e.event_id === id))}</Loadable>}
      <PageButtons primary="Zum Audit-Verzeichnis" primaryTo={back} testId="audit-event" />
    </>
  );
}

const DATE_LOCALES = { de, fr, it, en: enGB };

function DateFilter({ value, onChange, label, lang, testid }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const locale = DATE_LOCALES[lang] || de;
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={testid} className="h-10 w-[176px] justify-start gap-2 font-normal">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {selected ? format(selected, "dd.MM.yyyy", { locale }) : <span className="text-muted-foreground">{t("audit.date_placeholder")}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={selected} locale={locale} initialFocus
            onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
          {value && (
            <div className="border-t p-2">
              <Button variant="ghost" size="sm" className="w-full gap-1.5" data-testid={`${testid}-clear`}
                onClick={() => { onChange(""); setOpen(false); }}>
                <X className="h-3.5 w-3.5" /> {label}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
